import { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { GenericSvg } from '../../assets/svgs/GenericSVG';
import {
  EventIndicatorStateType,
  getEventIndicatorData,
  useOnboardingStore,
} from '../../features/webAppOnboarding/store/onboardingStore';
import { colors } from '../../styles/colors';
import { Body } from '../../styles/stringStyles';

export const EventIndicator = () => {
  const eventIndicatorData = useOnboardingStore(getEventIndicatorData);

  const [isVisible, setIsVisible] = useState(false);
  const [currentEventIndex, setCurrentEventIndex] = useState(eventIndicatorData?.index ?? 0);
  const timeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!eventIndicatorData?.index || eventIndicatorData?.index === currentEventIndex) return;
    if (timeout.current) clearTimeout(timeout.current);
    setCurrentEventIndex(eventIndicatorData?.index ?? 0);
    setIsVisible(true);
    timeout.current = setTimeout(() => {
      setIsVisible(false);
    }, 4500);
  }, [eventIndicatorData?.index, currentEventIndex]);

  const handleClick = () => {
    setIsVisible(false);
  };

  const eventIndicatorType: EventIndicatorStateType = eventIndicatorData?.type as EventIndicatorStateType;

  if (!eventIndicatorData) return null;

  return (
    <PositionContainer>
      <IndicatorContainer
        $eventType={eventIndicatorType}
        $isVisible={isVisible}
        onClick={handleClick}
        onTouchMove={handleClick}
      >
        <StyledBody>{eventIndicatorData?.message}</StyledBody>
        <GenericSvg
          name={eventIndicatorType === 'error' ? 'Close' : eventIndicatorType === 'info' ? 'Info' : 'Checkmark'}
          height={24}
          primaryColor={colors.white}
        />
      </IndicatorContainer>
    </PositionContainer>
  );
};

const StyledBody = styled(Body)`
  color: ${colors.white};
`;

const slideInUp = keyframes`
  from {
    transform: translate(0, 0);
    opacity: 0;
  }
  to {
    transform: translate(0, 30px);
    opacity: 1;
  }
`;

const slideOutDown = keyframes`
  from {
    transform: translate(0, 30px);
    opacity: 1;
  }
  to {
    transform: translate(0, 0);
    opacity: 0;
  }
`;

const IndicatorContainer = styled.div<{
  $eventType: EventIndicatorStateType;
  $isVisible: boolean;
}>`
  background-color: ${({ $eventType }) =>
    $eventType === 'error' ? colors.lightRed : $eventType === 'info' ? colors.medium : colors.primary};
  padding: 10px 20px;
  border-radius: 10px;
  gap: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  opacity: 0;
  visibility: ${(props) => (props.$isVisible ? 'visible' : 'hidden')};
  pointer-events: all;
  transition: visibility 0.5s;
  ${(props) =>
    props.$isVisible
      ? css`
          animation: ${slideInUp} 0.5s ease-in-out forwards;
        `
      : css`
          animation: ${slideOutDown} 0.5s ease-in-out forwards;
        `}
`;

const PositionContainer = styled.div`
  padding: 20px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  pointer-events: none;
`;
